"use client"
import React, { useRef } from 'react'
import { Button } from '@/components/ui/button'
import { CheckCircle, MessageSquare, Package, PoundSterling } from 'lucide-react'
import { motion, useScroll, useTransform } from 'framer-motion'
import AnimatedText from "@/components/ui/animated-text"
import { SectionTitle } from "@/components/ui/section-title"
import { useDialog } from '@/contexts/dialog-context'
import { navigateToContact } from '@/lib/scroll-utils'

export default function Process() {
  const scrollSection = useRef<HTMLDivElement>(null)
  const { openDialog } = useDialog()
  
  const { scrollYProgress } = useScroll({
    target: scrollSection,
    offset: ["start 50%", "end 30%"]
  })
  
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"])
  
  return (
    <section id="our-process" className='wrapper lg:pt-large pb-medium overflow-hidden bg-gradient-to-br from-foreground to-foreground/90'>
      {/* Background Text with Character-by-Character Fill Animation */}
      <motion.div 
        className="absolute -top-[4.5vw] -left-[0.25vw]"
        initial={{
          y: -80
        }}
        whileInView={{
          y: 0,
          transition: {
            duration: 0.3
          }
        }}
      >
        <AnimatedText 
          text="OUR SIMPLE PROCESS"
          className="font-extrabold uppercase text-[8.75vw] whitespace-nowrap"
          offsetStart="start 0.9"
          offsetEnd="start 0.25"
        />
      </motion.div>

      <div className='flex flex-col lg:flex-row items-start justify-center lg:justify-between w-full max-w-screen-2xl gap-16 py-medium mx-auto'>
        
        {/* Sticky Left Sidebar - Heading & CTA */}
        <div className='flex flex-col w-full lg:max-w-lg lg:sticky lg:top-48 h-fit text-background'>
          <SectionTitle className="text-body text-background/70">
            How It Works
          </SectionTitle>
          <h2 className='text-h1 text-balance w-full text-background '>
            Sell Your Luxury Watch in <span className=''>3 Simple Steps</span>
          </h2>
          <p className='text-body pt-4 text-background/80'>
            From initial valuation to final payment, our streamlined process ensures you get the best value for your timepiece with complete transparency and security.
          </p>
          
          {/* CTA Row */}
          <div className='flex items-start md:items-center justify-start w-full gap-4 pt-8 pb-small'>
            <Button 
              onClick={openDialog}
              className="bg-cta text-background hover:bg-cta/90"
            >
              Get Free Quote
            </Button>
            <Button 
              onClick={navigateToContact}
              variant='outline' 
              className='bg-transparent text-background border-background/30 hover:bg-background/90'
            >
              Arrange your visit
            </Button>
          </div>
        </div>

        {/* Scrolling Right Content - Process Steps */}
        <div className='flex flex-col w-full items-end'>

          <div className='flex flex-col w-fit relative' ref={scrollSection}>
            {/* Timeline Line Background */}
            <div className='absolute left-4 md:left-5 top-6 bottom-6 w-0.5 bg-cta/30 z-0'></div>
            
            {/* Animated Progress Line */}
            <div className='absolute left-4 md:left-5 top-6 bottom-6 w-0.5 z-[1]'>
              <motion.div 
                className='bg-cta brightness-200 w-0.5'
                style={{ 
                  height,
                  willChange: 'height',
                  transform: 'translate3d(0, 0, 0)'
                }}
              />
            </div>
            
            <div className='space-y-8 relative z-10'>
              {processSteps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div key={step.title} className='flex items-start gap-4 md:gap-8'>
                    {/* Timeline Dot */}
                    <div className='flex flex-col items-center flex-shrink-0'>
                      <div className='size-8 md:size-10 bg-foreground rounded-full flex items-center justify-center border-[1.75px] border-background shadow-lg'>
                        <IconComponent className='size-3 md:size-4 text-background' />
                      </div>
                    </div>
                    
                    {/* Step Content Card */}
                    <div className='max-w-2xl flex flex-col items-start justify-between w-full gap-4 bg-background/95 border-border/30 border-[1.75px] px-6 pt-6 pb-7 rounded-xl shadow-md hover:shadow-lg hover:border-cta/20 transition-all duration-300 flex-1'>
                      <div className='flex w-full items-start justify-between'>
                        <div className='flex items-center gap-4'>
                          <SectionTitle variant="step">STEP {index + 1}</SectionTitle>
                        </div>
                        <div className='text-xs text-muted-foreground'>{step.timeframe}</div>
                      </div>
                      <div className='flex flex-col justify-start w-full text-left gap-3'>
                        <h3 className='w-full text-h3 text-balance text-foreground'>{step.title}</h3>
                        <p className='w-full text-base text-pretty leading-relaxed text-muted-foreground'>{step.description}</p>
                        {step.features && (
                          <div className='mt-2'>
                            <ul className='space-y-1'>
                              {step.features.map((feature, idx) => (
                                <li key={idx} className='flex items-start gap-2 text-sm text-muted-foreground'>
                                  <CheckCircle className='size-4 text-cta mt-0.5 flex-shrink-0' />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>    
    </section>
  )
}


const processSteps = [
    {
        icon: MessageSquare,
        title: "Get Your Free Quote",
        timeframe: "Within 24 hours",
        description: "Submit details of your luxury watch through our secure online form or visit our showroom for an in-person consultation.",
        features: [
            "Expert initial assessment",
            "No obligation quote",
            "Professional consultation"
        ]
    },
    {
        icon: Package,
        title: "Visit Us or Ship",
        timeframe: "2-3 business days",
        description: "Bring your watch to our Canary Wharf (London) location or ship to us for detailed authentication and final valuation by certified experts.",
        features: [
            "15+ Years Experience",
            "Certified authentication",
            "Detailed inspection",
        ]
    },
    {
        icon: PoundSterling,
        title: "Get Paid Instantly",
        timeframe: "Same day payment",
        description: "Once you accept our final offer, receive immediate payment via your preferred method - bank transfer or cash.",
        features: [
            "Same-day processing",
            "Secure transactions",
            "No hidden fees"
        ]
    },
];
